import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useStoreActions } from 'easy-peasy';
import { useNavigate } from 'react-router-dom';
import { dateFormat } from '../../tools/manager';

import AttentionIcon from '../../../assets/images/svg/attention.svg';
import Modal from '../Modal_2';
import Search from '../../../assets/images/svg/search.svg';
import Table from '../Table';

const ListClassifyOrderCoupon = () => {
  const navigate = useNavigate();

  // =======================
  // STATES
  // =======================
  const [showModalMove, setShowModalMove] = useState(false);
  const [orderCoupons, setOrderCoupons] = useState([]);
  const [listOrderCoupons, setListOrderCoupons] = useState([]);
  const [textSearch, setTextSearch] = useState('');

  const [idCoupon, setIdCoupon] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [worksites, setWorksites] = useState([]);
  const [selectedWorksite, setSelectedWorksite] = useState('');

  const [nextToken, setNextToken] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // =======================
  // STORE ACTIONS
  // =======================
  const fetchOrderCoupons = useStoreActions((a) => a.middleware.OrdersMiddleware.getListOrderCoupon);
  const fetchWorksites = useStoreActions((a) => a.middleware.OrdersMiddleware.getListWorksitesClassify);
  const fetchCompanies = useStoreActions((a) => a.middleware.OrdersMiddleware.getListCompanyClassify);
  const moveCoupon = useStoreActions((a) => a.middleware.OrdersMiddleware.moveOrderCouponToWorksite);
  const fetchUser = useStoreActions((a) => a.middleware.AdminMiddleware.getAdmin);

  // =======================
  // FORMAT DATE
  // =======================
  const formatDate = (date) => {
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
  };

  // =======================
  // FETCH FIRST PAGE
  // =======================
  const getOrderCoupons = useCallback(async () => {
    try {
      const res = await fetchOrderCoupons({ limit: 25, nextToken: null });

      const items = res?.items || [];
      const token = res?.nextToken || null;

      // enrichissement collaborateurs
      const enriched = await Promise.all(
        items.map(async (el) => {
          if (!el?.collaborator?.first_name && el?.orderCouponCollaboratorId) {
            const user = await fetchUser(el.orderCouponCollaboratorId);
            return {
              ...el,
              collaborator: {
                first_name: user?.first_name || '',
                last_name: user?.last_name || '',
              },
            };
          }
          return el;
        })
      );

      setOrderCoupons(enriched);
      setListOrderCoupons(enriched);
      setNextToken(token);
    } catch {
      toast.error('Erreur lors de la récupération des bons de commande');
    }
  }, [fetchOrderCoupons, fetchUser]);

  // =======================
  // LOAD MORE (pagination)
  // =======================
  const loadMore = async () => {
    if (!nextToken) return;

    setLoadingMore(true);
    try {
      const res = await fetchOrderCoupons({ limit: 25, nextToken });

      const newItems = res?.items || [];
      const newToken = res?.nextToken || null;

      const enriched = await Promise.all(
        newItems.map(async (el) => {
          if (!el?.collaborator?.first_name && el?.orderCouponCollaboratorId) {
            const user = await fetchUser(el.orderCouponCollaboratorId);
            return {
              ...el,
              collaborator: {
                first_name: user?.first_name || '',
                last_name: user?.last_name || '',
              },
            };
          }
          return el;
        })
      );

      const merged = [...orderCoupons, ...enriched];

      setOrderCoupons(merged);
      setListOrderCoupons(merged);
      setNextToken(newToken);
    } catch {
      toast.error('Erreur lors du chargement supplémentaire');
    } finally {
      setLoadingMore(false);
    }
  };

  // =======================
  // FETCH COMPANIES
  // =======================
  const getCompanies = async () => {
    try {
      const res = await fetchCompanies();
      setCompanies(res[0]?.companies?.items || []);
    } catch {
      toast.error('Erreur lors de la récupération des clients');
    }
  };

  // =======================
  // SEARCH
  // =======================
  useEffect(() => {
    if (!textSearch) {
      setListOrderCoupons(orderCoupons);
    } else {
      const filtered = orderCoupons.filter((el) =>
        [
          el?.nameClient,
          el?.nameWorksite,
          el?.collaborator?.type?.name,
          el?.collaborator?.first_name,
          el?.collaborator?.last_name,
          el?.numero,
          dateFormat(el?.createdAt).day,
          dateFormat(el?.createdAt).month,
          dateFormat(el?.createdAt).year,
        ]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(textSearch.toLowerCase()))
      );
      setListOrderCoupons(filtered);
    }
  }, [textSearch, orderCoupons]);

  // =======================
  // INIT
  // =======================
  useEffect(() => {
    getOrderCoupons();
    getCompanies();
  }, [getOrderCoupons]);

  // =======================
  // MOVING COUPON
  // =======================
  const handleMove = (id) => {
    setIdCoupon(id);
    setShowModalMove(true);
  };

  const handleCompanyChange = (e) => {
    const company = JSON.parse(e.target.value);
    setWorksites(company?.worksites?.items || []);
    setSelectedWorksite('');
  };

  const handleConfirmMove = async () => {
    try {
      await moveCoupon({ id: idCoupon, idWorksite: selectedWorksite });
      toast.success('Bon de commande déplacé avec succès');
      setShowModalMove(false);
      getOrderCoupons();
    } catch {
      toast.error('Erreur lors du déplacement du bon de commande');
    }
  };

  const goToPreview = (id) => navigate(`/pdf-preview/order/${id}`);

  // =======================
  // RENDER
  // =======================
  return (
    <section className="md:pt-8 px-4 md:px-9">
      <div className="h-[500px]">
        {/* SEARCH */}
        <div className="relative flex mt-6 border border-gray-admin rounded-xl">
          <input
            type="text"
            placeholder="Recherche bons"
            className="w-full py-3 pl-10 border-none rounded-xl placeholder:text-blue-admin inputFocus"
            value={textSearch}
            onChange={(e) => setTextSearch(e.target.value)}
          />
          <img src={Search} alt="search icon" className="absolute left-0 w-8 h-5 pl-3 top-4" />
        </div>

        {/* TABLE */}
        <div className="overflow-auto pt-14 w-full">
          <Table>
            <thead className="h-10">
              <tr>
                <th>Réalisé le</th>
                <th>Fait par</th>
                <th>Numéro</th>
                <th>Type de chantier</th>
                <th>Nom du client</th>
                <th>Nom du chantier</th>
                <th>Déplacer vers</th>
              </tr>
            </thead>

            <tbody>
              {listOrderCoupons.length ? (
                listOrderCoupons.map((coupon) => (
                  <tr key={coupon.id} className="h-16 border-b border-white-admin hover:bg-blue-50 transition-all cursor-pointer">
                    <td onClick={() => goToPreview(coupon.id)}>{formatDate(coupon.createdAt)}</td>
                    <td onClick={() => goToPreview(coupon.id)}>
                      {coupon?.collaborator?.first_name} {coupon?.collaborator?.last_name}
                    </td>
                    <td onClick={() => goToPreview(coupon.id)}>{coupon?.numero}</td>
                    <td onClick={() => goToPreview(coupon.id)}>{coupon?.collaborator?.type?.name}</td>
                    <td onClick={() => goToPreview(coupon.id)}>{coupon?.nameClient}</td>
                    <td onClick={() => goToPreview(coupon.id)}>{coupon?.nameWorksite}</td>

                    <td>
                      <button
                        className="text-blue-admin border py-2 px-3 rounded-lg"
                        onClick={() => handleMove(coupon.id)}
                      >
                        Déplacer votre bon
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center text-lg font-semibold pt-20">
                    {textSearch ? "Oups, ce chantier ou client n'existe pas 🤭" : 'Ajouter un bon de commande'}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {/* LOAD MORE */}
          {nextToken && (
            <div className="flex justify-center my-8">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="px-5 py-2 border border-blue-admin rounded-xl text-blue-admin hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {loadingMore ? 'Chargement…' : 'Charger plus'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* MODAL */}
      <Modal isVisible={showModalMove} styleModal="max-w-fit">
        <div className="pt-5 pl-5">
          <img className="pl-5" src={AttentionIcon} alt="attention icon" />
          <p className="pl-5 pt-7">Déplacer le bon de commande</p>

          <div className="flex space-x-6 pl-5 py-5">
            {/* Client */}
            <select
              onChange={handleCompanyChange}
              className="cursor-pointer w-[200px] text-blue-admin text-sm bg-blue-dropdown py-3 pl-5 pr-10 rounded-lg border-none inputFocus"
              defaultValue=""
            >
              <option value="" disabled>
                Client
              </option>
              {companies.map((company) => (
                <option key={company.id} value={JSON.stringify(company)}>
                  {company?.name}
                </option>
              ))}
            </select>

            {/* Chantier */}
            <select
              value={selectedWorksite}
              onChange={(e) => setSelectedWorksite(e.target.value)}
              className="cursor-pointer w-[200px] text-blue-admin text-sm bg-blue-dropdown py-3 pl-5 pr-10 rounded-lg border-none inputFocus"
            >
              <option value="" disabled>
                Chantier
              </option>
              {worksites.map((ws) => (
                <option key={ws.id} value={ws.id}>
                  {ws?.name}
                </option>
              ))}
            </select>
          </div>

          <p className="px-5 pt-2 text-gray-admin">
            Êtes-vous sûr de vouloir déplacer ce bon ? Cette action est définitive.
          </p>

          <div className="text-center pt-7">
            <button
              className="px-12 py-3 mr-4 font-semibold border rounded-xl text-red-error"
              onClick={() => setShowModalMove(false)}
            >
              Annuler
            </button>

            <button
              disabled={!selectedWorksite}
              onClick={handleConfirmMove}
              className={`px-10 py-3 text-white rounded-xl ${selectedWorksite ? 'bg-red-error' : 'bg-gray-placeholder'}`}
            >
              Valider
            </button>
          </div>
        </div>
      </Modal>
    </section>
  );
};

export default ListClassifyOrderCoupon;
